/**
 * useActorAICreation Hook
 *
 * Orchestration hook for CreateActorWithAIDialog.
 * Manages form state, validation, and submission to AI webhook.
 *
 * Features:
 * - Form state management (name, audienceId, prompt)
 * - Source data selection state (paths + hydrated data)
 * - Validation with error messages
 * - Submission with loading state
 * - Integration with ActorAIService
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { ActorAIService } from '../services/ActorAIService';
import type {
  UseActorAICreationOptions,
  UseActorAICreationResult,
  ActorFormData,
  ActorFormErrors,
  HydratedSourceData,
} from '../types';

// =============================================================================
// CONSTANTS
// =============================================================================

const INITIAL_FORM_DATA: ActorFormData = {
  name: '',
  audienceId: '',
  prompt: '',
};

const INITIAL_HYDRATED_SOURCE_DATA: HydratedSourceData = {
  audiences: [],
  tasks: [],
};

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Strip HTML tags and get plain text
 */
function htmlToPlainText(html: string): string {
  // Create a temporary element to parse HTML
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * useActorAICreation Hook
 *
 * @param options - Hook configuration options
 * @returns Hook result with state, actions, and computed values
 */
export function useActorAICreation(
  options: UseActorAICreationOptions
): UseActorAICreationResult {
  const {
    projectId,
    currentUser,
    project,
    audiences,
    actorsByAudience,
    tasks,
    studyCases,
    onSuccess,
    onError,
    showNotification,
  } = options;

  // ===========================================================================
  // STATE
  // ===========================================================================

  const [formData, setFormData] = useState<ActorFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<ActorFormErrors>({});
  const [selectedPaths, setSelectedPaths] = useState<string[]>([]);
  const [hydratedSourceData, setHydratedSourceData] = useState<HydratedSourceData>(
    INITIAL_HYDRATED_SOURCE_DATA
  );
  const [hydratedPrompt, setHydratedPrompt] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // ===========================================================================
  // SERVICE
  // ===========================================================================

  const service = useMemo(() => new ActorAIService(), []);

  // ===========================================================================
  // VALIDATION
  // ===========================================================================

  /**
   * Validate form data
   */
  const validate = useCallback((): boolean => {
    const newErrors: ActorFormErrors = {};

    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = 'Actor name is required';
    }

    // Validate audienceId
    if (!formData.audienceId) {
      newErrors.audienceId = 'Please select a target audience';
    }

    // Validate prompt
    const plainTextPrompt = htmlToPlainText(formData.prompt);
    if (!plainTextPrompt.trim()) {
      newErrors.prompt = 'AI prompt is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // ===========================================================================
  // COMPUTED
  // ===========================================================================

  /**
   * Whether form is valid (computed from form data)
   */
  const isValid = useMemo(() => {
    const hasName = formData.name.trim().length > 0;
    const hasAudience = formData.audienceId.length > 0;
    const hasPrompt = htmlToPlainText(formData.prompt).trim().length > 0;
    return hasName && hasAudience && hasPrompt;
  }, [formData]);

  // ===========================================================================
  // ACTIONS
  // ===========================================================================

  /**
   * Reset form to initial state
   */
  const reset = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    setSelectedPaths([]);
    setHydratedSourceData(INITIAL_HYDRATED_SOURCE_DATA);
    setHydratedPrompt('');
    setIsSubmitting(false);
  }, []);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async () => {
    // Validate first
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Get selected audience details
      const selectedAudience = service.getAudienceById(formData.audienceId, audiences);
      if (!selectedAudience) {
        throw new Error('Selected audience not found');
      }

      // Build complete source data
      const completeSourceData = service.buildCompleteSourceData(
        hydratedSourceData,
        project
      );

      // Get processed prompt (use hydratedPrompt if available, otherwise process)
      const processedPrompt = hydratedPrompt || htmlToPlainText(formData.prompt);

      // Build payload
      const payload = service.buildPayload({
        name: formData.name,
        audienceId: formData.audienceId,
        audienceName: selectedAudience.name,
        audienceDescription: selectedAudience.description || '',
        processedPrompt,
        sourceData: completeSourceData,
        userId: currentUser.uid,
        projectId,
      });

      // Call AI service
      const result = await service.createActor(payload);

      if (result.success) {
        // Show success notification
        showNotification?.(
          'AI Actor creation started! This may take several minutes...',
          'info'
        );
        // Call success callback
        onSuccess?.();
      } else {
        // Show error notification
        const errorMessage = result.error || 'Failed to create actor';
        showNotification?.(errorMessage, 'error');
        onError?.(errorMessage);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred';
      console.error('[useActorAICreation] Error:', error);
      showNotification?.(errorMessage, 'error');
      onError?.(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validate,
    formData,
    hydratedSourceData,
    hydratedPrompt,
    audiences,
    project,
    currentUser.uid,
    projectId,
    service,
    onSuccess,
    onError,
    showNotification,
  ]);

  // ===========================================================================
  // RETURN
  // ===========================================================================

  return {
    // Form state
    formData,
    setFormData,
    errors,

    // Source data selection
    selectedPaths,
    setSelectedPaths,
    hydratedSourceData,
    setHydratedSourceData,

    // Prompt hydration
    hydratedPrompt,
    setHydratedPrompt,

    // Actions
    validate,
    handleSubmit,
    reset,

    // State
    isSubmitting,
    isValid,
  };
}
